import React, { useCallback, useEffect, useState } from "react";

import api from "../services/api";
import { qlAlert, qlConfirm } from "../utils/dialogs";
import {
  STATUT_DEMANDE, disponibilitesLisibles, fcfa, initiales, libelleModalites, lieuEnseignant, liste, messageErreur,
  structureEnseignant, tarifMinimum, MODALITES, LIBELLE_MODALITE,
} from "./soutien/soutienCommun";
import "./soutien/soutien.css";
import { SeancesParent } from "./soutien/Seances";
import { BoutonParametres } from "./Parametres";

// Portail public du soutien scolaire (/soutien) : annuaire consultable sans
// compte, fiches enseignants, compte parent demandé seulement pour solliciter,
// suivi des demandes. Aucune coordonnée privée n'est affichée.

const CLE_PARENT = "ql_parent_user";
const CLE_RETOUR = "ql_soutien_apres_connexion";
const PAR_PAGE = 12;
// Demande mise en relation par l'administration : le parent déclare ses séances et peut noter.
const EN_RELATION = ["confirmed", "completed"];
const MESSAGE_ATTENTE = "Vous pourrez noter cet enseignant et déclarer vos séances dès que l'administration vous aura mis en relation (demande confirmée).";

// ── Navigation (adresses /soutien/…, bouton Retour du navigateur) ───────────
function lireAdresse(pathname = window.location.pathname) {
  const morceaux = pathname.replace(/^\/soutien\/?/, "").split("/").filter(Boolean);
  if (morceaux[0] === "enseignants" && morceaux[1]) return { vue: morceaux[2] === "demande" ? "demande" : "profil", id: morceaux[1] };
  const vues = { "": "accueil", enseignants: "annuaire", "comment-ca-marche": "comment", connexion: "connexion", inscription: "inscription", "mes-demandes": "demandes" };
  return { vue: vues[morceaux[0] || ""] || "accueil", id: null };
}

function adresseDe(vue, id) {
  const chemins = { accueil: "/soutien", annuaire: "/soutien/enseignants", comment: "/soutien/comment-ca-marche", connexion: "/soutien/connexion",
    inscription: "/soutien/inscription", demandes: "/soutien/mes-demandes", profil: `/soutien/enseignants/${id}`, demande: `/soutien/enseignants/${id}/demande` };
  return chemins[vue] || "/soutien";
}

function lireParent() {
  try { return JSON.parse(localStorage.getItem(CLE_PARENT)); } catch { return null; }
}

// ── Petits composants ────────────────────────────────────────────────────────
function Avatar({ enseignant, grand = false }) {
  return enseignant.photo_url
    ? <img className={`sc-avatar${grand ? " sc-avatar--grand" : ""}`} src={enseignant.photo_url} alt="" />
    : <span className={`sc-avatar${grand ? " sc-avatar--grand" : ""}`} aria-hidden="true">{initiales(enseignant.full_name)}</span>;
}

function Pastilles({ elements }) {
  return <div className="sc-pastilles">{elements.map((e) => <span className="sc-pill" key={e}>{e}</span>)}</div>;
}

function Etat({ chargement, erreur, onReessayer, vide, children }) {
  if (chargement) return <div className="sc-etat" role="status"><span className="spinner-border spinner-border-sm" /> Chargement…</div>;
  if (erreur) return <div className="sc-etat sc-etat--erreur" role="alert">{erreur} {onReessayer && <button type="button" className="sc-btn sc-btn--outline" onClick={onReessayer}>Réessayer</button>}</div>;
  if (vide) return <div className="sc-etat">{vide}</div>;
  return children;
}

// Cinq étoiles remplies selon une note sur 5 (la moyenne peut remplir une étoile en partie).
export function Etoiles({ valeur, taille = "" }) {
  const pourcent = Math.max(0, Math.min(5, Number(valeur) || 0)) * 20;
  return (
    <span className={`sc-etoiles-note ${taille}`.trim()} role="img" aria-label={`${String(valeur ?? 0).replace(".", ",")} sur 5`}>
      <span className="sc-etoiles-vides" aria-hidden="true">★★★★★</span>
      <span className="sc-etoiles-pleines" style={{ width: `${pourcent}%` }} aria-hidden="true">★★★★★</span>
    </span>
  );
}

// Note Quick Learning (attribuée par le CEO) et moyenne des notes données par les parents, chacune sous son nom.
function Note({ rating, noteQl = null }) {
  const parents = rating?.count
    ? <span className="sc-note"><Etoiles valeur={rating.average} /> {String(rating.average).replace(".", ",")}/5 · {rating.count} avis de parents</span>
    : null;
  if (!noteQl) return parents || <span className="sc-note sc-note--vide"><Etoiles valeur={0} /> Pas encore d'avis</span>;
  return (
    <span className="sc-notes">
      <span className="sc-note sc-note--ql"><Etoiles valeur={noteQl} /> {noteQl}/5 · Note Quick Learning</span>
      {parents}
    </span>
  );
}

function CarteEnseignant({ enseignant, aller }) {
  const tarif = tarifMinimum(enseignant);
  return (
    <article className="sc-card sc-enseignant">
      <div className="sc-personne">
        <Avatar enseignant={enseignant} />
        <div>
          <span className="sc-badge">✓ Profil validé</span>
          <h3>{enseignant.full_name}</h3>
          {lieuEnseignant(enseignant) && <span className="sc-petit sc-muted"><i className="bi bi-geo-alt" aria-hidden="true"></i> {lieuEnseignant(enseignant)}</span>}
        </div>
      </div>
      {structureEnseignant(enseignant) && <div className="sc-petit sc-muted">{structureEnseignant(enseignant)}</div>}
      <Pastilles elements={enseignant.subjects.map((s) => s.name)} />
      {enseignant.classes.length > 0 && <div className="sc-petit sc-muted">Classes : {enseignant.classes.slice(0, 3).map((c) => c.name).join(" · ")}{enseignant.classes.length > 3 ? " …" : ""}</div>}
      {enseignant.modalities.length > 0 && <div className="sc-petit sc-muted">{libelleModalites(enseignant.modalities).join(" · ")}</div>}
      <Note rating={enseignant.rating} noteQl={enseignant.ceo_rating} />
      {tarif && <div className="sc-prix">À partir de {fcfa(tarif)}/h</div>}
      <div className="sc-actions">
        <button type="button" className="sc-btn sc-btn--outline" onClick={() => aller("profil", enseignant.id)}>Voir le profil</button>
        <button type="button" className="sc-btn sc-btn--or" onClick={() => aller("demande", enseignant.id)}>Solliciter</button>
      </div>
    </article>
  );
}

// ── Pages ────────────────────────────────────────────────────────────────────
function Accueil({ aller }) {
  const [etat, setEtat] = useState({ chargement: true, erreur: "", items: [] });
  const charger = useCallback(() => {
    setEtat({ chargement: true, erreur: "", items: [] });
    api.get("parent-teacher/directory/", { params: { page_size: 3 } })
      .then((r) => setEtat({ chargement: false, erreur: "", items: liste(r.data) }))
      .catch((e) => setEtat({ chargement: false, erreur: messageErreur(e), items: [] }));
  }, []);
  useEffect(charger, [charger]);
  return (
    <section>
      <div className="sc-hero">
        <div>
          <div className="sc-surtitre">ACCOMPAGNEMENT SCOLAIRE • CAMEROUN</div>
          <h1>Le bon professeur pour accompagner la réussite de votre enfant.</h1>
          <p className="sc-intro">Découvrez des enseignants aux profils validés, comparez leurs matières et leurs tarifs, puis envoyez votre demande de soutien en quelques clics.</p>
          <div className="sc-ligne">
            <button type="button" className="sc-btn sc-btn--or" onClick={() => aller("annuaire")}>Trouver un professeur →</button>
            <button type="button" className="sc-btn sc-btn--outline" onClick={() => aller("comment")}>Comment ça marche ?</button>
          </div>
          <p className="sc-petit sc-muted">✓ Profils examinés &nbsp; ✓ Demandes suivies &nbsp; ✓ Coordonnées privées protégées</p>
        </div>
        <div className="sc-hero-visuel">
          <div><i className="bi bi-person-video3" aria-hidden="true"></i><p>Un accompagnement adapté à chaque élève</p><span>À domicile · En ligne · Selon les disponibilités</span></div>
        </div>
      </div>
      <div className="sc-etapes">
        <div className="sc-stat"><strong>01</strong>Explorez les profils</div>
        <div className="sc-stat"><strong>02</strong>Choisissez un enseignant</div>
        <div className="sc-stat"><strong>03</strong>Envoyez votre demande</div>
      </div>
      <div className="sc-entete">
        <h2>Professeurs à découvrir</h2>
        <button type="button" className="sc-btn sc-btn--outline" onClick={() => aller("annuaire")}>Voir tout l'annuaire →</button>
      </div>
      <Etat chargement={etat.chargement} erreur={etat.erreur} onReessayer={charger} vide={!etat.items.length && "Les premiers profils validés apparaîtront ici très bientôt."}>
        <div className="sc-grille">{etat.items.map((e) => <CarteEnseignant key={e.id} enseignant={e} aller={aller} />)}</div>
      </Etat>
    </section>
  );
}

function CommentCaMarche({ aller }) {
  return (
    <section>
      <h2>Comment fonctionne le soutien scolaire ?</h2>
      <div className="sc-grille">
        <div className="sc-card"><h3>1. Recherchez</h3><p>Filtrez par matière, classe, ville et modalité. L'annuaire est consultable sans compte.</p></div>
        <div className="sc-card"><h3>2. Comparez</h3><p>Consultez les profils validés, les tarifs indiqués par les enseignants et leurs disponibilités.</p></div>
        <div className="sc-card"><h3>3. Demandez</h3><p>Créez votre compte parent puis envoyez votre demande : l'équipe Quick Learning organise la mise en relation et vous suivez son avancement.</p></div>
      </div>
      <p className="sc-notice">Les coordonnées des enseignants, des parents et des élèves ne sont jamais publiées. La mise en relation passe par une demande encadrée.</p>
      <button type="button" className="sc-btn sc-btn--or" onClick={() => aller("annuaire")}>Trouver un professeur →</button>
    </section>
  );
}

function Annuaire({ aller, matieres, classes }) {
  const [filtres, setFiltres] = useState({ subject: "", school_class: "", city: "", modality: "" });
  const [recherche, setRecherche] = useState(filtres);
  const [page, setPage] = useState(1);
  const [etat, setEtat] = useState({ chargement: true, erreur: "", items: [], total: 0 });
  const [essai, setEssai] = useState(0);

  useEffect(() => {
    let actif = true;
    setEtat((e) => ({ ...e, chargement: true, erreur: "" }));
    const params = Object.fromEntries(Object.entries({ ...recherche, page, page_size: PAR_PAGE }).filter(([, v]) => v !== ""));
    api.get("parent-teacher/directory/", { params })
      .then((r) => actif && setEtat({ chargement: false, erreur: "", items: liste(r.data), total: r.data?.count ?? liste(r.data).length }))
      .catch((e) => actif && setEtat({ chargement: false, erreur: messageErreur(e), items: [], total: 0 }));
    return () => { actif = false; };
  }, [recherche, page, essai]);

  const pages = Math.max(1, Math.ceil(etat.total / PAR_PAGE));
  const maj = (cle) => (e) => setFiltres((f) => ({ ...f, [cle]: e.target.value }));
  const lancer = (e) => { e.preventDefault(); setPage(1); setRecherche(filtres); };

  return (
    <section>
      <div className="sc-entete"><div>
        <div className="sc-surtitre">ANNUAIRE DES ENSEIGNANTS</div>
        <h2>Trouvez votre professeur idéal</h2>
        <p className="sc-muted">Consultez librement les profils. Un compte est requis uniquement pour solliciter un cours.</p>
      </div></div>
      <form className="sc-filtres" onSubmit={lancer} aria-label="Filtres de l'annuaire">
        <div><label htmlFor="sc-f-matiere">Matière</label><select id="sc-f-matiere" value={filtres.subject} onChange={maj("subject")}><option value="">Toutes les matières</option>{matieres.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
        <div><label htmlFor="sc-f-classe">Classe</label><select id="sc-f-classe" value={filtres.school_class} onChange={maj("school_class")}><option value="">Toutes les classes</option>{classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        <div><label htmlFor="sc-f-ville">Ville</label><input id="sc-f-ville" value={filtres.city} onChange={maj("city")} placeholder="Toutes les villes" /></div>
        <div><label htmlFor="sc-f-modalite">Modalité</label><select id="sc-f-modalite" value={filtres.modality} onChange={maj("modality")}><option value="">Toutes les modalités</option>{MODALITES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <button className="sc-btn sc-btn--navy sc-filtres-bouton">Rechercher</button>
      </form>
      {!etat.chargement && !etat.erreur && <p className="sc-petit sc-muted" aria-live="polite">{etat.total} enseignant(s) correspondant à votre recherche</p>}
      <Etat chargement={etat.chargement} erreur={etat.erreur} onReessayer={() => setEssai((n) => n + 1)}
        vide={!etat.items.length && "Aucun résultat pour ces filtres. Essayez une autre recherche."}>
        <div className="sc-grille">{etat.items.map((e) => <CarteEnseignant key={e.id} enseignant={e} aller={aller} />)}</div>
        {pages > 1 && (
          <nav className="sc-pagination" aria-label="Pages de l'annuaire">
            <button type="button" className="sc-btn sc-btn--outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Précédent</button>
            <span>Page {page} / {pages}</span>
            <button type="button" className="sc-btn sc-btn--outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Suivant →</button>
          </nav>
        )}
      </Etat>
    </section>
  );
}

function useEnseignant(id) {
  const [etat, setEtat] = useState({ chargement: true, erreur: "", enseignant: null });
  const [essai, setEssai] = useState(0);
  useEffect(() => {
    let actif = true;
    setEtat({ chargement: true, erreur: "", enseignant: null });
    api.get(`parent-teacher/directory/${id}/`)
      .then((r) => actif && setEtat({ chargement: false, erreur: "", enseignant: r.data }))
      .catch((e) => actif && setEtat({ chargement: false, erreur: e?.response?.status === 404 ? "Ce profil n'est pas disponible." : messageErreur(e), enseignant: null }));
    return () => { actif = false; };
  }, [id, essai]);
  return { ...etat, reessayer: () => setEssai((n) => n + 1) };
}

const LIBELLE_NOTE = ["", "Décevant", "Moyen", "Bien", "Très bien", "Excellent"];

// Le parent remplit de 1 à 5 étoiles pour un cours effectué ; la moyenne de l'enseignant est recalculée.
function FormulaireAvis({ demande, onEnvoye, titre = "Votre avis sur le cours effectué" }) {
  const [note, setNote] = useState(0);
  const [survol, setSurvol] = useState(0);
  const [commentaire, setCommentaire] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const affichee = survol || note;
  const envoyer = async (e) => {
    e.preventDefault();
    if (!note) { qlAlert("Choisissez une note avant de valider.", "error"); return; }
    setEnvoi(true);
    try {
      await api.post("parent-teacher/reviews/", { tutoring_request: demande.id, rating: note, comment: commentaire });
      qlAlert("Votre avis a bien été envoyé, merci !", "success");
      onEnvoye?.(note);
    } catch (err) { qlAlert(messageErreur(err), "error"); } finally { setEnvoi(false); }
  };
  return (
    <form className="sc-card sc-avis" onSubmit={envoyer}>
      <h3>{titre}</h3>
      <p className="sc-muted sc-petit">Touchez les étoiles pour donner une note de 1 à 5.</p>
      <div className="sc-ligne">
        <div className="sc-etoiles" role="radiogroup" aria-label="Note" onMouseLeave={() => setSurvol(0)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" role="radio" aria-checked={note === n} aria-label={`${n} sur 5`}
              className={n <= affichee ? "actif" : ""} onClick={() => setNote(n)} onMouseEnter={() => setSurvol(n)}>★</button>
          ))}
        </div>
        {affichee > 0 && <strong className="sc-note-choisie">{affichee}/5 · {LIBELLE_NOTE[affichee]}</strong>}
      </div>
      <label htmlFor={`sc-avis-texte-${demande.id}`}>Commentaire (50 caractères maximum)</label>
      <input id={`sc-avis-texte-${demande.id}`} maxLength={50} value={commentaire} onChange={(e) => setCommentaire(e.target.value.slice(0, 50))} />
      <button className="sc-btn sc-btn--or" disabled={envoi}>Envoyer mon avis</button>
    </form>
  );
}

function AvisParent({ enseignant, parent, onEnvoye }) {
  const [demande, setDemande] = useState(null);
  const [enAttente, setEnAttente] = useState(false);
  useEffect(() => {
    if (!parent) return;
    Promise.all([api.get("parent-teacher/requests/"), api.get("parent-teacher/reviews/")]).then(([r, a]) => {
      const notees = new Set(liste(a.data).map((x) => x.tutoring_request));
      const avecLui = liste(r.data).filter((x) => x.teacher.id === enseignant.id);
      setDemande(avecLui.find((x) => EN_RELATION.includes(x.status) && !notees.has(x.id)) || null);
      setEnAttente(avecLui.some((x) => x.status === "new" || x.status === "contacting"));
    }).catch(() => {});
  }, [parent, enseignant.id]);
  if (!demande) return enAttente ? <p className="sc-card sc-notice sc-avis">{MESSAGE_ATTENTE}</p> : null;
  return <FormulaireAvis demande={demande} titre={`Notez ${enseignant.full_name}`} onEnvoye={() => { setDemande(null); onEnvoye?.(); }} />;
}

function Profil({ id, aller, parent }) {
  const { chargement, erreur, enseignant, reessayer } = useEnseignant(id);
  return (
    <section>
      <button type="button" className="sc-btn sc-btn--lien" onClick={() => aller("annuaire")}>← Retour à l'annuaire</button>
      <Etat chargement={chargement} erreur={erreur} onReessayer={reessayer}>
        {enseignant && (() => {
          const tarif = tarifMinimum(enseignant);
          const tarifs = enseignant.subjects.filter((s) => Number(enseignant.hourly_rates?.[s.id]) > 0);
          const dispo = disponibilitesLisibles(enseignant.availability);
          return (
            <div className="sc-profil">
              <div className="sc-card">
                <div className="sc-profil-haut">
                  <Avatar enseignant={enseignant} grand />
                  <div>
                    <span className="sc-badge">✓ Profil validé</span>
                    <h2>{enseignant.full_name}</h2>
                    <p className="sc-muted">{[lieuEnseignant(enseignant), structureEnseignant(enseignant)].filter(Boolean).join(" · ")}</p>
                    <Note rating={enseignant.rating} noteQl={enseignant.ceo_rating} />
                  </div>
                </div>
                <hr />
                {enseignant.presentation && <><h3>À propos de l'enseignant</h3><p className="sc-intro">{enseignant.presentation}</p></>}
                <h3>Matières enseignées</h3>
                <Pastilles elements={enseignant.subjects.map((s) => (Number(enseignant.hourly_rates?.[s.id]) > 0 ? `${s.name} · ${fcfa(enseignant.hourly_rates[s.id])}/h` : s.name))} />
                <h3>Classes accompagnées</h3>
                <Pastilles elements={enseignant.classes.map((c) => c.name)} />
                <h3>Modalités</h3>
                <Pastilles elements={libelleModalites(enseignant.modalities)} />
                {dispo.length > 0 && <>
                  <h3>Disponibilités indiquées</h3>
                  <ul className="sc-dispos">{dispo.map(([jour, plage]) => <li key={jour}><strong>{jour}</strong> {plage}</li>)}</ul>
                </>}
                <p className="sc-notice">Les coordonnées personnelles ne sont pas publiées. La mise en relation passe par une demande encadrée sur Quick Learning.</p>
              </div>
              <aside className="sc-card sc-aside">
                {tarif ? <>
                  <div className="sc-muted sc-petit">Tarif indicatif déclaré</div>
                  <div className="sc-prix">À partir de {fcfa(tarif)}/h</div>
                  {tarifs.length > 1 && <ul className="sc-petit sc-muted sc-tarifs">{tarifs.map((s) => <li key={s.id}>{s.name} : {fcfa(enseignant.hourly_rates[s.id])}/h</li>)}</ul>}
                  <p className="sc-muted sc-petit">Le tarif et les conditions définitives sont à confirmer lors de la mise en relation.</p>
                </> : <p className="sc-muted sc-petit">Tarif non renseigné : il sera précisé lors de la mise en relation.</p>}
                <button type="button" className="sc-btn sc-btn--or sc-plein" onClick={() => aller("demande", enseignant.id)}>Solliciter cet enseignant →</button>
              </aside>
              <AvisParent enseignant={enseignant} parent={parent} onEnvoye={reessayer} />
            </div>
          );
        })()}
      </Etat>
    </section>
  );
}

function Connexion({ aller, onConnecte }) {
  const [champs, setChamps] = useState({ username: "", password: "" });
  const [oubli, setOubli] = useState(false);
  const [email, setEmail] = useState("");
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);

  const connecter = async (e) => {
    e.preventDefault(); setErreur(""); setEnvoi(true);
    try {
      const r = await api.post("parent-teacher/login/", champs);
      onConnecte(r.data);
    } catch (err) { setErreur(messageErreur(err)); } finally { setEnvoi(false); }
  };
  const envoyerLien = async (e) => {
    e.preventDefault(); setEnvoi(true);
    try {
      await api.post("password-reset/", { email });
      qlAlert("Si cet email est enregistré, un lien de réinitialisation vient de vous être envoyé.", "success");
      setOubli(false);
    } catch (err) { qlAlert(messageErreur(err), "error"); } finally { setEnvoi(false); }
  };

  return (
    <section className="sc-formulaire"><div className="sc-card">
      <div className="sc-surtitre">ESPACE PARENT</div>
      {oubli ? <>
        <h2>Mot de passe oublié</h2>
        <p className="sc-muted">Indiquez l'email utilisé lors de votre inscription : un lien de réinitialisation vous sera envoyé.</p>
        <form onSubmit={envoyerLien}>
          <div className="sc-champ"><label htmlFor="sc-oubli">Email</label><input id="sc-oubli" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" /></div>
          <button className="sc-btn sc-btn--navy sc-plein" disabled={envoi}>Envoyer le lien</button>
        </form>
        <button type="button" className="sc-btn sc-btn--lien" onClick={() => setOubli(false)}>← Retour à la connexion</button>
      </> : <>
        <h2>Heureux de vous retrouver !</h2>
        <p className="sc-muted">Connectez-vous pour envoyer et suivre vos demandes de soutien.</p>
        {erreur && <div className="sc-alerte" role="alert">{erreur}</div>}
        <form onSubmit={connecter}>
          <div className="sc-champ"><label htmlFor="sc-login">Nom d'utilisateur</label><input id="sc-login" required autoComplete="username" value={champs.username} onChange={(e) => setChamps((c) => ({ ...c, username: e.target.value }))} /></div>
          <div className="sc-champ"><label htmlFor="sc-mdp">Mot de passe</label><input id="sc-mdp" required type="password" autoComplete="current-password" value={champs.password} onChange={(e) => setChamps((c) => ({ ...c, password: e.target.value }))} /></div>
          <button className="sc-btn sc-btn--navy sc-plein" disabled={envoi}>{envoi ? "Connexion…" : "Se connecter"}</button>
        </form>
        <div className="sc-ligne">
          <button type="button" className="sc-btn sc-btn--lien" onClick={() => aller("inscription")}>Créer un compte parent</button>
          <button type="button" className="sc-btn sc-btn--lien" onClick={() => setOubli(true)}>Mot de passe oublié ?</button>
        </div>
      </>}
    </div></section>
  );
}

function Inscription({ aller, onConnecte }) {
  const [etape, setEtape] = useState(1);
  const [champs, setChamps] = useState({ full_name: "", phone: "", email: "", city: "", neighborhood: "", username: "", password: "", confirmation: "" });
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const maj = (cle) => (e) => setChamps((c) => ({ ...c, [cle]: e.target.value }));

  const suivant = (e) => { e.preventDefault(); setErreur(""); setEtape(2); };
  const creer = async (e) => {
    e.preventDefault(); setErreur("");
    if (champs.password.length < 8) { setErreur("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (champs.password !== champs.confirmation) { setErreur("Les deux mots de passe ne correspondent pas."); return; }
    setEnvoi(true);
    try {
      const { confirmation, ...donnees } = champs; // eslint-disable-line no-unused-vars
      await api.post("parent-teacher/register/", donnees);
      const r = await api.post("parent-teacher/login/", { username: champs.username, password: champs.password });
      qlAlert("Votre compte parent est créé.", "success");
      onConnecte(r.data);
    } catch (err) { setErreur(messageErreur(err)); } finally { setEnvoi(false); }
  };

  return (
    <section className="sc-formulaire sc-formulaire--large"><div className="sc-card">
      <div className="sc-surtitre">INSCRIPTION PARENT</div>
      <h2>Créez votre compte en quelques étapes</h2>
      <p className="sc-muted">Aucun code manuel requis. Ce compte n'ouvre pas l'accès aux contenus payants ni aux données scolaires d'un élève.</p>
      <div className="sc-etapes-form" aria-label={`Étape ${etape} sur 2`}>
        <div className={`sc-etape${etape === 1 ? " actif" : ""}`}>1. Vos coordonnées</div>
        <div className={`sc-etape${etape === 2 ? " actif" : ""}`}>2. Sécurité</div>
      </div>
      {erreur && <div className="sc-alerte" role="alert">{erreur}</div>}
      {etape === 1 ? (
        <form onSubmit={suivant}>
          <div className="sc-ligne-form">
            <div className="sc-champ"><label htmlFor="sc-nom">Nom complet</label><input id="sc-nom" required value={champs.full_name} onChange={maj("full_name")} autoComplete="name" maxLength={200} /></div>
            <div className="sc-champ"><label htmlFor="sc-tel">Téléphone</label><input id="sc-tel" required type="tel" value={champs.phone} onChange={maj("phone")} placeholder="+237 6XX XXX XXX" autoComplete="tel" maxLength={30} /></div>
          </div>
          <div className="sc-ligne-form">
            <div className="sc-champ"><label htmlFor="sc-email">Email</label><input id="sc-email" required type="email" value={champs.email} onChange={maj("email")} autoComplete="email" /></div>
            <div className="sc-champ"><label htmlFor="sc-ville">Ville</label><input id="sc-ville" required value={champs.city} onChange={maj("city")} maxLength={120} /></div>
          </div>
          <div className="sc-champ"><label htmlFor="sc-quartier">Quartier (facultatif)</label><input id="sc-quartier" value={champs.neighborhood} onChange={maj("neighborhood")} maxLength={120} /></div>
          <button className="sc-btn sc-btn--or">Continuer →</button>
        </form>
      ) : (
        <form onSubmit={creer}>
          <div className="sc-champ"><label htmlFor="sc-user">Nom d'utilisateur</label><input id="sc-user" required value={champs.username} onChange={maj("username")} autoComplete="username" maxLength={150} /></div>
          <div className="sc-ligne-form">
            <div className="sc-champ"><label htmlFor="sc-pass">Mot de passe</label><input id="sc-pass" required type="password" minLength={8} value={champs.password} onChange={maj("password")} autoComplete="new-password" placeholder="8 caractères minimum" /></div>
            <div className="sc-champ"><label htmlFor="sc-pass2">Confirmer le mot de passe</label><input id="sc-pass2" required type="password" value={champs.confirmation} onChange={maj("confirmation")} autoComplete="new-password" /></div>
          </div>
          <p className="sc-notice">Les informations sur votre enfant seront demandées uniquement lors d'une demande de cours, selon les besoins.</p>
          <div className="sc-ligne">
            <button type="button" className="sc-btn sc-btn--outline" onClick={() => setEtape(1)}>← Retour</button>
            <button className="sc-btn sc-btn--or" disabled={envoi}>{envoi ? "Création…" : "Créer mon compte"}</button>
          </div>
        </form>
      )}
      <p className="sc-petit sc-muted">Déjà inscrit ? <button type="button" className="sc-btn sc-btn--lien" onClick={() => aller("connexion")}>Se connecter</button></p>
    </div></section>
  );
}

function Demande({ id, aller }) {
  const { chargement, erreur, enseignant, reessayer } = useEnseignant(id);
  const [champs, setChamps] = useState({ student_name: "", school_class: "", subject_ids: [], modality: "", location: "", preferred_slots: "", message: "" });
  const [erreurs, setErreurs] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const maj = (cle) => (e) => setChamps((c) => ({ ...c, [cle]: e.target.value }));

  useEffect(() => {
    if (!enseignant) return;
    setChamps((c) => ({
      ...c,
      subject_ids: enseignant.subjects.length === 1 ? [enseignant.subjects[0].id] : c.subject_ids,
      school_class: enseignant.classes.length === 1 ? String(enseignant.classes[0].id) : c.school_class,
      modality: enseignant.modalities.length === 1 ? enseignant.modalities[0] : c.modality,
    }));
  }, [enseignant]);

  const basculerMatiere = (id) => setChamps((c) => ({
    ...c, subject_ids: c.subject_ids.includes(id) ? c.subject_ids.filter((x) => x !== id) : [...c.subject_ids, id],
  }));

  const envoyer = async (e) => {
    e.preventDefault(); setErreurs({});
    if (!champs.subject_ids.length) { setErreurs({ subject_ids: "Choisissez au moins une matière." }); return; }
    setEnvoi(true);
    try {
      await api.post("parent-teacher/requests/", { ...champs, teacher_profile_id: enseignant.id });
      qlAlert("Votre demande a bien été envoyée. Vous pouvez suivre son avancement dans « Mes demandes ».", "success");
      aller("demandes");
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data === "object" && !data.detail) setErreurs(Object.fromEntries(Object.entries(data).map(([k, v]) => [k, [].concat(v)[0]])));
      else qlAlert(messageErreur(err), "error");
    } finally { setEnvoi(false); }
  };
  const aide = (cle) => erreurs[cle] && <div className="sc-erreur-champ" id={`sc-err-${cle}`}>{erreurs[cle]}</div>;
  const invalide = (cle) => (erreurs[cle] ? { "aria-invalid": true, "aria-describedby": `sc-err-${cle}` } : {});

  return (
    <section className="sc-formulaire sc-formulaire--large">
      <button type="button" className="sc-btn sc-btn--lien" onClick={() => aller("profil", id)}>← Retour au profil</button>
      <Etat chargement={chargement} erreur={erreur} onReessayer={reessayer}>
        {enseignant && (
          <div className="sc-card">
            <div className="sc-surtitre">DEMANDE DE SOUTIEN</div>
            <h2>Organisons le soutien de votre enfant</h2>
            <p>Enseignant sélectionné : <strong>{enseignant.full_name}</strong></p>
            <form onSubmit={envoyer} noValidate={false}>
              <div className="sc-ligne-form">
                <div className="sc-champ"><label htmlFor="sc-eleve">Prénom ou initiales de l'élève</label><input id="sc-eleve" required maxLength={120} value={champs.student_name} onChange={maj("student_name")} placeholder="Ex. : Amina" {...invalide("student_name")} />{aide("student_name")}</div>
                <div className="sc-champ"><label htmlFor="sc-classe">Classe</label><select id="sc-classe" required value={champs.school_class} onChange={maj("school_class")} {...invalide("school_class")}><option value="">Choisir une classe</option>{enseignant.classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>{aide("school_class")}</div>
              </div>
              <fieldset className="sc-champ" {...invalide("subject_ids")}>
                <legend className="sc-legende">Matières souhaitées <span className="sc-muted sc-petit">(une ou plusieurs)</span></legend>
                <div className="sc-cases">
                  {enseignant.subjects.map((s) => (
                    <label key={s.id} className="sc-case">
                      <input type="checkbox" name="matieres" value={s.id} checked={champs.subject_ids.includes(s.id)} onChange={() => basculerMatiere(s.id)} /> {s.name}
                      {Number(enseignant.hourly_rates?.[s.id]) > 0 && <span className="sc-muted"> · {fcfa(enseignant.hourly_rates[s.id])}/h</span>}
                    </label>
                  ))}
                </div>
                {aide("subject_ids")}
              </fieldset>
              <div className="sc-ligne-form">
                <div className="sc-champ"><label htmlFor="sc-modalite">Modalité</label><select id="sc-modalite" required value={champs.modality} onChange={maj("modality")} {...invalide("modality")}><option value="">Choisir une modalité</option>{enseignant.modalities.map((m) => <option key={m} value={m}>{LIBELLE_MODALITE[m] || m}</option>)}</select>{aide("modality")}</div>
              </div>
              <div className="sc-ligne-form">
                <div className="sc-champ"><label htmlFor="sc-lieu">Ville / quartier</label><input id="sc-lieu" required maxLength={200} value={champs.location} onChange={maj("location")} placeholder="Ex. : Yaoundé, Bastos" {...invalide("location")} />{aide("location")}</div>
                <div className="sc-champ"><label htmlFor="sc-creneaux">Disponibilités</label><input id="sc-creneaux" required maxLength={200} value={champs.preferred_slots} onChange={maj("preferred_slots")} placeholder="Ex. : mercredi après-midi" {...invalide("preferred_slots")} />{aide("preferred_slots")}</div>
              </div>
              <div className="sc-champ"><label htmlFor="sc-objectif">Objectif du soutien (facultatif)</label><textarea id="sc-objectif" rows={4} maxLength={2000} value={champs.message} onChange={maj("message")} placeholder="Difficultés, objectifs, fréquence souhaitée…" /></div>
              <p className="sc-notice">Votre demande est visible uniquement par les personnes autorisées à la traiter. Les coordonnées privées ne sont pas rendues publiques. N'indiquez ni adresse exacte ni nom complet de l'enfant.</p>
              <button className="sc-btn sc-btn--or" disabled={envoi}>{envoi ? "Envoi…" : "Envoyer ma demande →"}</button>
            </form>
          </div>
        )}
      </Etat>
    </section>
  );
}

function SuiviDemande({ demande }) {
  const [messages, setMessages] = useState(null);
  const [texte, setTexte] = useState("");
  const charger = useCallback(() => {
    if (!demande.conversation) { setMessages([]); return; }
    api.get(`messages/${demande.conversation}/`).then((r) => setMessages(r.data.messages || [])).catch(() => setMessages([]));
  }, [demande.conversation]);
  useEffect(charger, [charger]);
  const envoyer = async (e) => {
    e.preventDefault();
    try { await api.post(`messages/${demande.conversation}/`, { content: texte, msg_type: "text" }); setTexte(""); charger(); }
    catch (err) { qlAlert(messageErreur(err), "error"); }
  };
  return (
    <div className="sc-suivi">
      <dl className="sc-details">
        {demande.student_name && <><dt>Élève</dt><dd>{demande.student_name}</dd></>}
        {demande.location && <><dt>Lieu</dt><dd>{demande.location}</dd></>}
        {demande.preferred_slots && <><dt>Disponibilités</dt><dd>{demande.preferred_slots}</dd></>}
        {demande.message && <><dt>Objectif</dt><dd>{demande.message}</dd></>}
      </dl>
      <h4>Échange avec l'équipe Quick Learning</h4>
      {messages === null ? <div className="sc-etat">Chargement…</div> : (
        <div className="sc-messages">
          {messages.length === 0 && <p className="sc-muted sc-petit">Aucun message pour le moment.</p>}
          {messages.map((m) => <p key={m.id}><strong>{m.sender_name || "Interlocuteur"} :</strong> <span style={{ whiteSpace: "pre-wrap" }}>{m.content}</span></p>)}
        </div>
      )}
      {demande.conversation
        ? <form className="sc-ligne" onSubmit={envoyer}><input required aria-label="Votre message" value={texte} onChange={(e) => setTexte(e.target.value)} placeholder="Votre message" /><button className="sc-btn sc-btn--navy">Envoyer</button></form>
        : <p className="sc-muted sc-petit">Un membre de l'équipe sera associé à cette demande.</p>}
    </div>
  );
}

function MesDemandes({ aller, parent, onDeconnexion }) {
  const [etat, setEtat] = useState({ chargement: true, erreur: "", items: [] });
  const [ouverte, setOuverte] = useState(null);
  const [notes, setNotes] = useState({});  // demande → note déjà donnée
  const charger = useCallback(() => {
    setEtat((e) => ({ ...e, chargement: true, erreur: "" }));
    api.get("parent-teacher/requests/")
      .then((r) => setEtat({ chargement: false, erreur: "", items: liste(r.data) }))
      .catch((e) => setEtat({ chargement: false, erreur: messageErreur(e), items: [] }));
    api.get("parent-teacher/reviews/")
      .then((r) => setNotes(Object.fromEntries(liste(r.data).map((a) => [a.tutoring_request, a.rating]))))
      .catch(() => {});
  }, []);
  useEffect(charger, [charger]);

  const annuler = (demande) => qlConfirm("Annuler cette demande de soutien ?", async () => {
    try { await api.post(`parent-teacher/requests/${demande.id}/cancel/`); qlAlert("Demande annulée.", "success"); charger(); }
    catch (err) { qlAlert(messageErreur(err), "error"); }
  });

  const items = etat.items;
  const enAttente = items.filter((d) => d.status === "new" || d.status === "contacting").length;
  const confirmees = items.filter((d) => d.status === "confirmed" || d.status === "completed").length;
  return (
    <div className="sc-tableau">
      <aside className="sc-menu">
        <h3>Espace parent</h3>
        <p className="sc-petit">{parent?.full_name}</p>
        <button type="button" className="actif">Mes demandes</button>
        <button type="button" onClick={() => aller("annuaire")}>Trouver un professeur</button>
        <button type="button" onClick={onDeconnexion}>Déconnexion</button>
      </aside>
      <section>
        <div className="sc-entete">
          <div><div className="sc-surtitre">ESPACE PARENT</div><h2>Mes demandes de soutien</h2><p className="sc-muted">Retrouvez vos demandes et leur état d'avancement.</p></div>
          <button type="button" className="sc-btn sc-btn--or" onClick={() => aller("annuaire")}>+ Nouvelle demande</button>
        </div>
        <div className="sc-etapes">
          <div className="sc-stat"><strong>{items.length}</strong>Demandes</div>
          <div className="sc-stat"><strong>{enAttente}</strong>En attente</div>
          <div className="sc-stat"><strong>{confirmees}</strong>Confirmées</div>
        </div>
        <Etat chargement={etat.chargement} erreur={etat.erreur} onReessayer={charger}
          vide={!items.length && <>Vous n'avez pas encore de demande. <button type="button" className="sc-btn sc-btn--lien" onClick={() => aller("annuaire")}>Trouver un professeur</button></>}>
          {items.map((d) => (
            <article className={`sc-card sc-demande sc-demande--${d.status}`} key={d.id}>
              <div className="sc-ligne sc-entre">
                <strong>{d.subject_name || "Soutien"} · {d.teacher.full_name}</strong>
                <span className={`sc-pill sc-statut--${d.status}`}>{STATUT_DEMANDE[d.status] || d.status}</span>
              </div>
              <p className="sc-muted sc-petit">{[d.class_name, LIBELLE_MODALITE[d.modality] || d.modality, `envoyée le ${new Date(d.created_at).toLocaleDateString("fr-FR")}`].filter(Boolean).join(" · ")}</p>
              <div className="sc-ligne">
                <button type="button" className="sc-btn sc-btn--outline" aria-expanded={ouverte === d.id} onClick={() => setOuverte(ouverte === d.id ? null : d.id)}>{ouverte === d.id ? "Masquer le suivi" : "Voir le suivi"}</button>
                {d.can_cancel && <button type="button" className="sc-btn sc-btn--danger" onClick={() => annuler(d)}>Annuler la demande</button>}
              </div>
              {(d.status === "new" || d.status === "contacting") && <p className="sc-notice sc-petit">{MESSAGE_ATTENTE}</p>}
              {EN_RELATION.includes(d.status) && <SeancesParent demande={d} />}
              {EN_RELATION.includes(d.status) && (notes[d.id]
                ? <p className="sc-note">Votre note : <Etoiles valeur={notes[d.id]} /> {notes[d.id]}/5</p>
                : <FormulaireAvis demande={d} titre={`Notez ${d.teacher.full_name}`}
                    onEnvoye={(n) => setNotes((x) => ({ ...x, [d.id]: n }))} />)}
              {ouverte === d.id && <SuiviDemande demande={d} />}
            </article>
          ))}
        </Etat>
      </section>
    </div>
  );
}

// ── Portail ─────────────────────────────────────────────────────────────────
export default function ParentTeacherPortal() {
  const [config, setConfig] = useState(null);
  const [parent, setParent] = useState(lireParent);
  const [adresse, setAdresse] = useState(() => lireAdresse());
  const [matieres, setMatieres] = useState([]);
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    api.get("parent-teacher/config/").then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
    api.get("matieres/").then((r) => setMatieres(liste(r.data))).catch(() => {});
    api.get("classes/").then((r) => setClasses(liste(r.data))).catch(() => {});
    const retour = () => setAdresse(lireAdresse());
    window.addEventListener("popstate", retour);
    return () => window.removeEventListener("popstate", retour);
  }, []);

  // Parent connecté : signal de présence toutes les minutes, pour la supervision temps réel.
  useEffect(() => {
    if (!parent) return undefined;
    // Le signal ne doit jamais gêner le portail : toute erreur est ignorée.
    const signal = () => Promise.resolve().then(() => api.post("ping/")).catch(() => {});
    signal();
    const minuterie = setInterval(signal, 60000);
    return () => clearInterval(minuterie);
  }, [parent]);

  const aller = useCallback((vue, id = null) => {
    // Solliciter ou suivre ses demandes demande un compte parent : on y revient après connexion.
    if ((vue === "demande" || vue === "demandes") && !lireParent()) {
      try { sessionStorage.setItem(CLE_RETOUR, JSON.stringify({ vue, id })); } catch { /* stockage indisponible */ }
      vue = "connexion"; id = null;
    }
    window.history.pushState({}, "", adresseDe(vue, id));
    setAdresse({ vue, id });
    window.scrollTo(0, 0);
  }, []);

  const connecte = (donnees) => {
    localStorage.setItem("access_token", donnees.access);
    localStorage.setItem("refresh_token", donnees.refresh);
    localStorage.setItem(CLE_PARENT, JSON.stringify(donnees.user));
    setParent(donnees.user);
    let suite = { vue: "demandes", id: null };
    try { suite = JSON.parse(sessionStorage.getItem(CLE_RETOUR)) || suite; sessionStorage.removeItem(CLE_RETOUR); } catch { /* stockage indisponible */ }
    aller(suite.vue, suite.id);
  };

  const deconnexion = () => {
    api.post("logout/").catch(() => {});
    [CLE_PARENT, "access_token", "refresh_token"].forEach((cle) => localStorage.removeItem(cle));
    setParent(null);
    aller("accueil");
  };

  if (!config) return <div className="sc-portail"><div className="sc-etat" role="status">Chargement…</div></div>;
  if (!config.enabled) {
    return <div className="pt-disabled"><h1>Espace parents et soutien scolaire</h1><p>Cette nouveauté est désactivée sur cet environnement.</p><a href="/">Retour</a></div>;
  }

  // Page protégée ouverte directement (lien, actualisation) sans être connecté.
  let { vue } = adresse;
  if ((vue === "demande" || vue === "demandes") && !parent) vue = "connexion";
  if ((vue === "connexion" || vue === "inscription") && parent) vue = "demandes";

  const lien = (cible, libelle) => (
    <button type="button" className={vue === cible ? "actif" : ""} aria-current={vue === cible ? "page" : undefined} onClick={() => aller(cible)}>{libelle}</button>
  );

  return (
    <div className="sc-portail">
      <header className="sc-tete">
        <a className="sc-marque" href="/" aria-label="Quick Learning — accueil du site">
          <img src="/static/logo.png" alt="Quick Learning" />
          <span>SOUTIEN SCOLAIRE</span>
        </a>
        <nav aria-label="Soutien scolaire">
          {lien("accueil", "Accueil")}
          {lien("annuaire", "Enseignants")}
          {lien("demandes", "Mes demandes")}
          <BoutonParametres />
          {parent
            ? <button type="button" onClick={deconnexion}>Déconnexion</button>
            : <>{lien("connexion", "Connexion")}<button type="button" className="sc-btn sc-btn--or" onClick={() => aller("inscription")}>Créer un compte</button></>}
        </nav>
      </header>
      <main className="sc-contenu">
        {vue === "accueil" && <Accueil aller={aller} />}
        {vue === "comment" && <CommentCaMarche aller={aller} />}
        {vue === "annuaire" && <Annuaire aller={aller} matieres={matieres} classes={classes} />}
        {vue === "profil" && <Profil id={adresse.id} aller={aller} parent={parent} />}
        {vue === "connexion" && <Connexion aller={aller} onConnecte={connecte} />}
        {vue === "inscription" && <Inscription aller={aller} onConnecte={connecte} />}
        {vue === "demande" && <Demande id={adresse.id} aller={aller} />}
        {vue === "demandes" && <MesDemandes aller={aller} parent={parent} onDeconnexion={deconnexion} />}
      </main>
      <footer className="sc-pied">Quick Learning · Soutien scolaire · Profils validés et demandes encadrées</footer>
    </div>
  );
}
